import { DEFAULT_HELP_CONTENT } from "../content/helpContent.js";
import { DEFAULT_HELP_CONTENT_EN } from "../content/helpContent.en.js";
import { useRef, useEffect, useState, useMemo } from "react";
import {
  Search, ChevronDown, ChevronRight, Plus, Trash2, ArrowUp, ArrowDown,
  Bold, Italic, List, ListOrdered, Link2, Heading1, Heading2,
  Eraser, BookOpen, X, Save, GripVertical,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Help / FAQ center — owner-facing "how to use the system" documentation.
// Content is authored by the superadmin in the admin portal (HelpEditor),
// stored in platform_config.helpContent, and rendered publicly at /help and
// inside the owner portal. One module so App.jsx stays thin.
// ─────────────────────────────────────────────────────────────────────────────

// Default seed content (zh-HK, owner-facing). Used until the admin saves their
// own. Shape: { title, subtitle, sections: [{ id, title, items: [{ id, title, body }] }] }
// `body` is trusted HTML authored only by the superadmin via the WYSIWYG editor.
// Screenshots live in /public/features (demo shop, phone numbers redacted).

const uid = () => "h" + Math.random().toString(36).slice(2, 9);
// Strip tags for search matching.
const plain = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/&[a-z]+;/gi, " ");

// Shared CSS for rendered help bodies + accordion. Scoped under .pb-help.
const HELP_CSS = `
.pb-help { --pb-ink:#2C2A4A; --pb-soft:#6B6A8A; --pb-line:#ECEAF6; --pb-accent:#7C3AED; }
.pb-help .pb-help-body { color:#3A3850; font-size:15px; line-height:1.7; }
.pb-help .pb-help-body p { margin:0 0 12px; }
.pb-help .pb-help-body h2 { font-size:18px; font-weight:900; color:var(--pb-ink); margin:18px 0 8px; }
.pb-help .pb-help-body h3 { font-size:16px; font-weight:800; color:var(--pb-ink); margin:14px 0 6px; }
.pb-help .pb-help-body ul, .pb-help .pb-help-body ol { margin:0 0 12px; padding-left:22px; }
.pb-help .pb-help-body li { margin:0 0 7px; }
.pb-help .pb-help-body a { color:var(--pb-accent); text-decoration:underline; font-weight:700; }
.pb-help .pb-help-body strong { color:var(--pb-ink); font-weight:800; }
.pb-help .pb-help-body img { max-width:100%; border-radius:12px; }
.pb-help .pb-shot { margin:14px auto 18px; max-width:300px; text-align:center; }
.pb-help .pb-shot img { width:100%; border-radius:22px; border:1px solid var(--pb-line); box-shadow:0 12px 30px rgba(44,42,74,0.14); }
.pb-help .pb-shot figcaption { font-size:12.5px; font-weight:800; color:var(--pb-soft); margin-top:8px; }
.pb-help .pb-help-body ol > figure { margin-left:-22px; }
.pb-topics { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:10px; }
.pb-topic { display:flex; align-items:center; gap:12px; background:#fff; border:1px solid var(--pb-line); border-radius:16px; padding:12px 14px; text-decoration:none; transition:box-shadow .2s, transform .2s; }
.pb-topic:hover { box-shadow:0 8px 28px rgba(124,58,237,0.12); transform:translateY(-1px); }
.pb-topic-img { width:52px; height:52px; border-radius:12px; flex-shrink:0; background-size:cover; background-position:center 42%; border:1px solid var(--pb-line); }
.pb-acc { border:1px solid var(--pb-line); border-radius:16px; overflow:hidden; background:#fff; margin-bottom:10px; transition:box-shadow .2s; }
.pb-acc[data-open="true"] { box-shadow:0 8px 28px rgba(124,58,237,0.10); }
.pb-acc-q { width:100%; text-align:left; background:none; border:none; cursor:pointer; padding:16px 18px; display:flex; align-items:center; justify-content:space-between; gap:12px; font-size:15.5px; font-weight:800; color:var(--pb-ink); }
.pb-acc-a { padding:0 18px 16px; }
.pb-acc-chev { transition:transform .25s; flex-shrink:0; color:var(--pb-soft); }
.pb-acc[data-open="true"] .pb-acc-chev { transform:rotate(180deg); }
`;

// Toolbar button — module-level so it isn't recreated on every editor render
// (a nested component would remount the contentEditable and drop the caret).
function Tool({ on, title, onClick, children }) {
  return (
    <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      style={{ width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:9,
        border:"1px solid " + (on ? "#7C3AED" : "#E3E0F0"), background:on ? "#F3EEFE" : "#fff",
        color:on ? "#7C3AED" : "#5A5876", cursor:"pointer", padding:0 }}>{children}</button>
  );
}

// WYSIWYG rich-text editor (dependency-free, execCommand). Admin-only.
export function RichTextEditor({ value, onChange, minHeight = 140 }) {
  const ref = useRef(null);
  const [active, setActive] = useState({});

  // Sync external value in without clobbering the caret while typing.
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== (value || "")) el.innerHTML = value || "";
  }, [value]);

  const emit = () => onChange?.(ref.current?.innerHTML || "");
  const refreshActive = () => {
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        ul: document.queryCommandState("insertUnorderedList"),
        ol: document.queryCommandState("insertOrderedList"),
      });
    } catch { /* ignore */ }
  };
  const exec = (cmd, arg) => {
    ref.current?.focus();
    try { document.execCommand(cmd, false, arg); } catch { /* ignore */ }
    emit(); refreshActive();
  };
  const block = (tag) => {
    // Toggle: if already that block, revert to a paragraph. Use the bracketed
    // tag form ("<h2>") — Firefox/WebKit require it; Chrome accepts both.
    let cur = "";
    try { cur = document.queryCommandValue("formatBlock"); } catch { /* ignore */ }
    const isSame = cur.toLowerCase() === tag.toLowerCase();
    exec("formatBlock", isSame ? "<p>" : "<" + tag.toLowerCase() + ">");
  };
  const addLink = () => {
    const url = window.prompt("連結網址 (URL)：", "https://");
    if (url) exec("createLink", url);
  };

  return (
    <div style={{ border:"1px solid #E3E0F0", borderRadius:14, overflow:"hidden", background:"#fff" }}>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, padding:8, borderBottom:"1px solid #EEEBF8", background:"#FBFAFF" }}>
        <Tool on={active.bold} title="粗體" onClick={() => exec("bold")}><Bold size={16} /></Tool>
        <Tool on={active.italic} title="斜體" onClick={() => exec("italic")}><Italic size={16} /></Tool>
        <Tool title="大標題" onClick={() => block("H2")}><Heading1 size={16} /></Tool>
        <Tool title="小標題" onClick={() => block("H3")}><Heading2 size={16} /></Tool>
        <Tool on={active.ul} title="項目符號" onClick={() => exec("insertUnorderedList")}><List size={16} /></Tool>
        <Tool on={active.ol} title="編號清單" onClick={() => exec("insertOrderedList")}><ListOrdered size={16} /></Tool>
        <Tool title="連結" onClick={addLink}><Link2 size={16} /></Tool>
        <Tool title="清除格式" onClick={() => exec("removeFormat")}><Eraser size={16} /></Tool>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning
        onInput={emit} onKeyUp={refreshActive} onMouseUp={refreshActive} onBlur={emit}
        className="pb-help"
        style={{ minHeight, padding:"12px 14px", outline:"none", fontSize:15, lineHeight:1.7, color:"#3A3850" }} />
    </div>
  );
}

// Public / in-portal viewer. An index of topics (grouped, daily work first)
// and one page per topic at /help/<id>, with previous / next.
const topicFromUrl = () => {
  if (typeof window === "undefined") return null;
  const m = (window.location.pathname || "").match(/^(?:\/en)?\/help\/([a-z0-9-]+)\/?$/i)
    || (window.location.hash || "").match(/^#\/help\/([a-z0-9-]+)/i);
  return m ? m[1] : null;
};
const firstImg = (html) => (String(html || "").match(/<img[^>]+src="([^"]+)"/) || [])[1] || null;
const summaryOf = (it) => it.summary || plain(it.body).replace(/\s+/g, " ").trim().slice(0, 60);

export function HelpCenter({ content, onBack, lang = "zh", embedded = false }) {
  const isZh = lang !== "en";
  // The admin-edited override (platform_config.helpContent) is Chinese; the
  // English centre always uses the built-in English text.
  const data = !isZh ? DEFAULT_HELP_CONTENT_EN
    : content && Array.isArray(content.sections) && content.sections.length ? content : DEFAULT_HELP_CONTENT;
  const base = isZh ? "/help" : "/en/help";
  const [q, setQ] = useState("");
  const [topicId, setTopicId] = useState(topicFromUrl);
  useEffect(() => {
    const on = () => setTopicId(topicFromUrl());
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  const go = (id) => {
    try { window.history.pushState({}, "", id ? base + "/" + id : base); } catch { /* ignore */ }
    setTopicId(id); setQ("");
    window.scrollTo(0, 0);
  };

  const flat = useMemo(() => data.sections.flatMap(s => (s.items || []).map(it => ({ ...it, section: s }))), [data]);
  const topic = topicId ? flat.find(it => it.id === topicId) : null;
  useEffect(() => {
    if (embedded) return;
    try {
      document.title = topic ? topic.title + (isZh ? "｜PettoBook 使用教學" : " | PettoBook Help Centre") : (isZh ? "寵物美容店系統使用教學 · PettoBook" : "Help Centre · PettoBook");
      document.documentElement.lang = isZh ? "zh-HK" : "en";
    } catch { /* ignore */ }
  }, [topic, isZh, embedded]);

  const sections = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.sections;
    return data.sections
      .map(s => ({ ...s, items: (s.items || []).filter(it =>
        (it.title + " " + (it.summary || "") + " " + plain(it.body)).toLowerCase().includes(needle)) }))
      .filter(s => s.items.length);
  }, [data, q]);
  const searching = q.trim().length > 0;

  const header = (
    <div style={{ background:"linear-gradient(135deg,#5B21B6 0%,#7C3AED 60%,#9333EA 100%)", color:"#fff",
      padding: embedded ? "28px 20px 64px" : "34px 20px 72px", borderRadius:"0 0 28px 28px" }}>
      <div style={{ maxWidth:880, margin:"0 auto" }}>
        {(onBack || topic) && (
          <button onClick={topic ? () => go(null) : onBack} style={{ background:"rgba(255,255,255,0.16)", border:"none", color:"#fff",
            borderRadius:99, padding:"7px 14px", fontSize:13, fontWeight:800, cursor:"pointer", marginBottom:16 }}>
            ← {topic ? (isZh ? "所有題目" : "All topics") : (isZh ? "返回" : "Back")}
          </button>
        )}
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ background:"rgba(255,255,255,0.15)", borderRadius:16, width:46, height:46, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <BookOpen size={24} color="#fff" strokeWidth={2.5} />
          </div>
          <div>
            {topic && <p style={{ margin:"0 0 2px", fontSize:13, opacity:0.85, fontWeight:800 }}>{data.title || "使用說明中心"} › {topic.section.title}</p>}
            <h1 style={{ margin:0, fontSize: topic ? 22 : 24, fontWeight:900 }}>{topic ? topic.title : (data.title || (isZh ? "使用說明中心" : "Help Center"))}</h1>
            {!topic && data.subtitle && <p style={{ margin:"4px 0 0", fontSize:14, opacity:0.92, fontWeight:600 }}>{data.subtitle}</p>}
          </div>
        </div>
      </div>
    </div>
  );

  if (topic) {
    const i = flat.findIndex(it => it.id === topic.id);
    const prev = flat[i - 1], next = flat[i + 1];
    const navBtn = (it, label, align) => it && (
      <button onClick={() => go(it.id)} style={{ flex:1, minWidth:0, textAlign:align, background:"#fff", border:"1px solid #ECEAF6", borderRadius:14, padding:"12px 14px", cursor:"pointer" }}>
        <span style={{ display:"block", fontSize:12, fontWeight:800, color:"#9A98B8" }}>{label}</span>
        <span style={{ display:"block", fontSize:14.5, fontWeight:900, color:"#2C2A4A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{it.title}</span>
      </button>
    );
    return (
      <div className="pb-help" style={{ minHeight:"100vh", background:"#FAF9FE", paddingBottom:60 }}>
        <style>{HELP_CSS}</style>
        {header}
        <div style={{ maxWidth:760, margin:"-44px auto 0", padding:"0 16px", position:"relative" }}>
          <article style={{ background:"#fff", borderRadius:20, boxShadow:"0 10px 36px rgba(91,33,182,0.10)", padding:"22px 20px" }}>
            {topic.summary && <p style={{ margin:"0 0 16px", fontSize:15.5, fontWeight:800, color:"#5B21B6", lineHeight:1.6 }}>{topic.summary}</p>}
            <div className="pb-help-body" dangerouslySetInnerHTML={{ __html: topic.body || "" }} />
          </article>
          <div style={{ display:"flex", gap:10, marginTop:16 }}>
            {navBtn(prev, isZh ? "← 上一題" : "← Previous", "left")}
            {navBtn(next, isZh ? "下一題 →" : "Next →", "right")}
          </div>
          <p style={{ textAlign:"center", margin:"22px 0 0" }}>
            <button onClick={() => go(null)} style={{ background:"none", border:"none", color:"#7C3AED", fontWeight:800, fontSize:14, cursor:"pointer", textDecoration:"underline" }}>{isZh ? "睇所有題目" : "See all topics"}</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-help" style={{ minHeight:"100vh", background:"#FAF9FE", paddingBottom:60 }}>
      <style>{HELP_CSS}</style>
      {header}
      <div style={{ maxWidth:880, margin:"-44px auto 0", padding:"0 16px", position:"relative" }}>
        <div style={{ background:"#fff", borderRadius:16, boxShadow:"0 10px 36px rgba(91,33,182,0.12)", padding:"6px 14px", display:"flex", alignItems:"center", gap:10, marginBottom:22 }}>
          <Search size={18} color="#9A98B8" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={isZh ? "搜尋問題或關鍵字…" : "Search…"}
            style={{ flex:1, border:"none", outline:"none", fontSize:15, fontWeight:600, color:"#3A3850", padding:"10px 0", background:"none" }} />
          {q && <button onClick={() => setQ("")} style={{ background:"none", border:"none", cursor:"pointer", color:"#9A98B8", display:"flex" }}><X size={16} /></button>}
        </div>

        {!searching && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:22 }}>
            {data.sections.map(s => (
              <a key={s.id} href={"#help-" + s.id} onClick={(e) => { e.preventDefault(); document.getElementById("help-" + s.id)?.scrollIntoView({ behavior:"smooth", block:"start" }); }}
                style={{ background:"#fff", border:"1px solid #ECEAF6", borderRadius:99, padding:"7px 14px", fontSize:13, fontWeight:800, color:"#5B21B6", textDecoration:"none", cursor:"pointer" }}>
                {s.title}
              </a>
            ))}
          </div>
        )}

        {sections.length === 0 && (
          <p style={{ textAlign:"center", color:"#9A98B8", fontWeight:700, padding:"40px 0" }}>
            {isZh ? "搵唔到相關內容" : "No results found"}
          </p>
        )}

        {sections.map(s => (
          <section key={s.id} id={"help-" + s.id} style={{ marginBottom:30, scrollMarginTop:16 }}>
            <h2 style={{ fontSize:18, fontWeight:900, color:"#2C2A4A", margin:"0 0 12px" }}>{s.title}</h2>
            <div className="pb-topics">
              {(s.items || []).map(it => {
                const img = firstImg(it.body);
                return (
                  <a key={it.id} href={base + "/" + it.id} onClick={(e) => { e.preventDefault(); go(it.id); }} className="pb-topic">
                    {img && <span className="pb-topic-img" style={{ backgroundImage:"url(" + img + ")" }} />}
                    <span style={{ flex:1, minWidth:0 }}>
                      <span style={{ display:"block", fontSize:15.5, fontWeight:900, color:"#2C2A4A" }}>{it.title}</span>
                      <span style={{ display:"block", fontSize:13, fontWeight:700, color:"#7A789A", marginTop:3, lineHeight:1.5 }}>{summaryOf(it)}</span>
                    </span>
                    <ChevronRight size={18} color="#C8C6DC" style={{ flexShrink:0 }} />
                  </a>
                );
              })}
            </div>
          </section>
        ))}

        <p style={{ textAlign:"center", color:"#B6B4CE", fontSize:12.5, fontWeight:700, margin:"30px 0 0" }}>PettoBook</p>
      </div>
    </div>
  );
}

// Admin editor — manage sections/items + edit bodies via WYSIWYG.
export function HelpEditor({ content, onChange }) {
  const data = content && Array.isArray(content.sections) ? content : DEFAULT_HELP_CONTENT;
  const set = (patch) => onChange?.({ ...data, ...patch });
  const setSections = (sections) => set({ sections });

  const updSection = (i, patch) => setSections(data.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const moveSection = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= data.sections.length) return;
    const arr = [...data.sections]; [arr[i], arr[j]] = [arr[j], arr[i]]; setSections(arr);
  };
  const addSection = () => setSections([...data.sections, { id: uid(), title: "新分類", items: [] }]);
  const delSection = (i) => { if (window.confirm("刪除呢個分類同入面所有內容？")) setSections(data.sections.filter((_, idx) => idx !== i)); };

  const updItem = (si, ii, patch) => updSection(si, { items: data.sections[si].items.map((it, idx) => idx === ii ? { ...it, ...patch } : it) });
  const moveItem = (si, ii, dir) => {
    const items = [...data.sections[si].items]; const j = ii + dir; if (j < 0 || j >= items.length) return;
    [items[ii], items[j]] = [items[j], items[ii]]; updSection(si, { items });
  };
  const addItem = (si) => updSection(si, { items: [...(data.sections[si].items || []), { id: uid(), title: "新問題", body: "" }] });
  const delItem = (si, ii) => { if (window.confirm("刪除呢條內容？")) updSection(si, { items: data.sections[si].items.filter((_, idx) => idx !== ii) }); };

  const lbl = { fontSize:12, fontWeight:800, color:"#8A8A9E", margin:"0 0 4px" };
  const inp = { width:"100%", border:"1px solid #E3E0F0", borderRadius:10, padding:"9px 12px", fontSize:14, fontWeight:700, color:"#3A3850", outline:"none", boxSizing:"border-box", background:"#fff" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", flexDirection:"column", gap:10, background:"#F8F7FE", borderRadius:14, padding:14 }}>
        <div><p style={lbl}>頁面標題</p><input style={inp} value={data.title || ""} onChange={e => set({ title: e.target.value })} placeholder="使用說明中心" /></div>
        <div><p style={lbl}>副標題</p><input style={inp} value={data.subtitle || ""} onChange={e => set({ subtitle: e.target.value })} placeholder="一句簡介…" /></div>
      </div>

      {data.sections.map((s, si) => (
        <div key={s.id} style={{ border:"1px solid #E9E6F6", borderRadius:16, padding:14, background:"#fff" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
            <GripVertical size={16} color="#C8C6DC" />
            <input style={{ ...inp, fontWeight:900, fontSize:15 }} value={s.title} onChange={e => updSection(si, { title: e.target.value })} placeholder="分類名稱" />
            <button title="上移" onClick={() => moveSection(si, -1)} style={ic}><ArrowUp size={16} /></button>
            <button title="下移" onClick={() => moveSection(si, 1)} style={ic}><ArrowDown size={16} /></button>
            <button title="刪除分類" onClick={() => delSection(si)} style={{ ...ic, color:"#EF4444" }}><Trash2 size={16} /></button>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:14, paddingLeft:4 }}>
            {(s.items || []).map((it, ii) => (
              <div key={it.id} style={{ borderLeft:"3px solid #EDE9FC", paddingLeft:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <input style={{ ...inp, fontWeight:800 }} value={it.title} onChange={e => updItem(si, ii, { title: e.target.value })} placeholder="問題 / 標題" />
                  <button title="上移" onClick={() => moveItem(si, ii, -1)} style={ic}><ArrowUp size={15} /></button>
                  <button title="下移" onClick={() => moveItem(si, ii, 1)} style={ic}><ArrowDown size={15} /></button>
                  <button title="刪除" onClick={() => delItem(si, ii)} style={{ ...ic, color:"#EF4444" }}><Trash2 size={15} /></button>
                </div>
                <RichTextEditor value={it.body} onChange={(html) => updItem(si, ii, { body: html })} />
              </div>
            ))}
            <button onClick={() => addItem(si)} style={addBtn}><Plus size={15} strokeWidth={3} /> 新增問題 / 內容</button>
          </div>
        </div>
      ))}

      <button onClick={addSection} style={{ ...addBtn, justifyContent:"center", padding:"12px", borderStyle:"solid", fontWeight:900 }}>
        <Plus size={16} strokeWidth={3} /> 新增分類
      </button>
      <p style={{ fontSize:12, color:"#9A98B8", fontWeight:700, margin:0 }}>
        💡 編輯完撳頁面底部嘅「儲存所有設定」先會生效。內容會喺 /help 頁面同店主後台顯示。
      </p>
    </div>
  );
}

const ic = { width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:8, border:"1px solid #E3E0F0", background:"#fff", color:"#7A789A", cursor:"pointer", flexShrink:0, padding:0 };
const addBtn = { display:"inline-flex", alignItems:"center", gap:6, background:"#F3EEFE", color:"#7C3AED", border:"1px dashed #C9B8F5", borderRadius:10, padding:"9px 14px", fontSize:13, fontWeight:800, cursor:"pointer" };
